/**
 * A placed piece of site-map equipment's name, as the viewer reads it.
 *
 * Placement used to store the AUTHOR's translated default ('Tower crane' /
 * 'タワークレーン'), freezing the chip in their language for every viewer. A
 * name that is still a default — blank, the type key itself (what a new
 * placement stores: the backend requires a non-empty name), or any locale's
 * default label for that type — now renders as the viewer's translation of
 * the type. A name someone actually chose ('Tower crane TC-1') renders as is.
 */

/** Every default label a client has rendered (and so stored) per type — the
 *  web `logistics.siteMap.equipment.*` strings, English and Japanese; their
 *  history holds no other variant. Frozen: a renamed label is ADDED here. */
const LEGACY_EQUIPMENT_DEFAULTS: Record<string, readonly string[]> = {
  towerCrane: ['Tower crane', 'タワークレーン'],
  mobileCrane: ['Mobile crane', '移動式クレーン'],
  generator: ['Generator', '発電機'],
  excavator: ['Excavator', '掘削機'],
  scissorLift: ['Scissor lift', '高所作業車'],
  concretePump: ['Concrete pump', 'コンクリートポンプ'],
};

/** Whether `name` is only the default for `type` (in any language), not a
 *  name someone chose. */
export function isDefaultEquipmentName(type: string, name: string | null | undefined): boolean {
  const trimmed = (name ?? '').trim();
  return trimmed === '' || trimmed === type || (LEGACY_EQUIPMENT_DEFAULTS[type] ?? []).includes(trimmed);
}

/** The chip's label (and aria-label): the viewer's translation of the type
 *  for a default name, the chosen name otherwise. */
export function equipmentDisplayName(
  t: (key: string) => string,
  equipment: { type: string; name: string | null | undefined },
): string {
  return isDefaultEquipmentName(equipment.type, equipment.name)
    ? t(`logistics.siteMap.equipment.${equipment.type}`)
    : equipment.name!.trim();
}
